export enum SourcingType {
  RFQ = 'RFQ',
  RFP = 'RFP'
}

export enum InvitationMode {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN'
}

export enum SourcingScope {
  NCB = 'NCB',
  ICB = 'ICB'
}

export enum SourcingStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  PUBLISHED = 'PUBLISHED',
  CLOSED = 'CLOSED',
  REJECTED = 'REJECTED',
  RESULT_SUBMITTED = 'RESULT_SUBMITTED',
  AWARD_APPROVED = 'AWARD_APPROVED',
  PARTIALLY_AWARDED = 'PARTIALLY_AWARDED',
  AWARDED = 'AWARDED'
}

export enum VisibleToSuppliers {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
  NONE = 'NONE'
}

export enum ApprovalStepStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum TechnicalEvaluationMode {
  NONE = 'NONE',
  PASS_FAIL = 'PASS_FAIL',
  MATRIX = 'MATRIX'
}

export enum EvaluationPhase {
  NOT_STARTED = 'NOT_STARTED',
  TECHNICAL_IN_PROGRESS = 'TECHNICAL_IN_PROGRESS',
  TECHNICAL_COMPLETED = 'TECHNICAL_COMPLETED',
  FINANCIAL_IN_PROGRESS = 'FINANCIAL_IN_PROGRESS',
  FINANCIAL_COMPLETED = 'FINANCIAL_COMPLETED',
  COMPLETE = 'COMPLETE'
}

export enum EvaluationCriteriaType {
  TECHNICAL = 'TECHNICAL',
  FINANCIAL = 'FINANCIAL'
}

export enum EvaluationStatus {
  QUALIFIED = 'QUALIFIED',
  DISQUALIFIED = 'DISQUALIFIED'
}

export interface CandidateItem {
  prId: string;
  prNumber: string;
  department: string;
  requester: string;
  itemId: string;
  itemName: string;
  description: string;
  categoryType: string;
  productCategory: string;
  unit: string;
  unitPrice: number;
  requestedQty: number;
  inSourcingQty: number;
  awardedQty: number;
  orderedQty: number;
  receivedQty: number;
  remainingQty: number;
}

export interface CartEntry {
  key: string;
  candidate: CandidateItem;
  selectedQty: number;
}

export interface BidItem {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  totalPrice: number;
  specifications?: string;
  tags?: string[];
  allocations?: SourcingItemAllocation[];
}

export interface SourcingItemAllocation {
  prId: string;
  prItemId: string;
  quantity: number;
}

export interface SourcingItemAward {
  id: string;
  sourcingItemId: string;
  bidId: string;
  supplierId: string;
  supplierName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  decision?: string;
  remarks?: string;
}

export interface EvaluationCriteria {
  id: string;
  name: string;
  description?: string;
  weight: number;
  visibleToSuppliers: VisibleToSuppliers;
  threshold?: number;
  type: EvaluationCriteriaType;
}

export interface BidEvaluationResult {
  criteriaId: string;
  value: number;
  remarks?: string;
  evaluatedBy?: string;
  evaluatedAt?: string;
  passed?: boolean;
}

export interface BidDetails {
  items: BidItem[];
  totalAmount: number;
  title: string;
  description: string;
  currency?: string;
  notes?: string;
  evaluationResults?: BidEvaluationResult[];
}

export interface Bid {
  id: string;
  supplierId: string;
  supplierName: string;
  companyName: string;
  sourcingId: string;
  supplierSourcingId: string;
  bidId: string;
  fields: Array<{
    fieldName: string;
    fieldValue: string;
  }>;
  documents: Array<{
    documentName: string;
    fileName: string;
    url: string;
  }>;
  items: Array<{
    itemName: string;
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
    deliveryTime: string;
    notes?: string;
    specifications?: string;
    prId?: string;
  }>;
  submissionDate: string;
  evaluationResults: BidEvaluationResult[];
  technicalScore?: number;
  financialScore?: number;
  finalScore: number;
  evaluationStatus?: EvaluationStatus;
  compliance?: boolean;
  decision?: string;
  evaluationRemarks?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface Attachment {
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
  url?: string;
  category?: string;
  createdBy?: string;
}

export interface Sourcing {
  id: string;
  sourcingNo: string;
  prIds: string[];
  title?: string;
  description?: string;
  type: SourcingType;
  sourcingScope: SourcingScope;
  invitationMode: InvitationMode;
  status: SourcingStatus;
  items: BidItem[];
  totalAmount: number;
  currency?: string;
  notes?: string;
  attachments?: Attachment[];
  evaluationCriterias?: EvaluationCriteria[];
  technicalEvaluationMode: TechnicalEvaluationMode;
  technicalEvaluationThreshold?: number;
  evaluationPhase: EvaluationPhase;
  createdBy?: string;
  submittedBy?: string;
  submittedAt?: string;
  approvedBy?: string;
  openedAt?: string;
  openedBy?: string;
  approvalType?: string;
  steps?: any[];
  approvals?: Array<{
    order: number;
    roleSlug: string;
    stepName?: string;
    approvedBy?: string;
    approvedOn?: Date;
    remarks?: string;
    stepStatus: ApprovalStepStatus;
  }>;
  awardApprovalType?: 'LOCAL' | 'EXTERNAL';
  awardApprovalFlow?: any;
  awardApprovals?: any[];
  resultSubmittedBy?: string;
  resultSubmittedAt?: string;
  awardedBy?: string;
  awardedAt?: string;
  externalWorkflow?: any;
  flowCaseId?: string;
  flowCaseNumber?: string;
  publishData?: {
    id: string;
    title: string;
    sourcingType: 'rfq' | 'rfp';
    biddingType: 'ncb' | 'icb';
    description: string;
    dueDate: string;
    status: 'open' | 'closed' | 'awarded';
    isPublic: boolean;
    template: {
      id: string;
      name: string;
    };
    invitedSuppliers: Array<{
      id: string;
      companyName: string;
    }>;
    fields: Array<{
      name: string;
      label: string;
      description: string;
      type: string;
      isRequired: boolean;
    }>;
    fileRequirements: Array<{
      name: string;
      label: string;
      description: string;
      documentType: string;
      isRequired: boolean;
    }>;
    createdAt?: string;
    createdBy?: {
      id: string;
      name: string;
    };
    openedAt?: string;
    openedBy?: {
      id: string;
      name: string;
    };
    bids?: Array<{
      id: string;
      supplier: {
        id: string;
        companyName: string;
      };
      fields: Array<{
        fieldName: string;
        fieldValue: string;
      }>;
      documents: Array<{
        id: string;
        documentName: string;
        fileName: string;
        url: string;
      }>;
      submissionDate: string;
      status: string;
      evaluationResults?: BidEvaluationResult[];
    }>;
  };
  poId?: string;
  poIds?: string[];
  awardLetter?: Attachment;
  awardLetters?: Attachment[];
  awardAcceptanceLetter?: Attachment;
  awardAcceptanceLetters?: Attachment[];
  itemAwards?: SourcingItemAward[];
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseRequest {
  id: string;
  requestNo: string;
  title?: string;
  description?: string;
  status: string;
  items: BidItem[];
  totalAmount: number;
  currency?: string;
  attachments?: Attachment[];
  evaluationCriterias?: EvaluationCriteria[];
}

// Form interfaces
export interface SourcingFormData {
  prIds: string[];
  title?: string;
  description?: string;
  type: SourcingType;
  sourcingScope: SourcingScope;
  invitationMode: InvitationMode;
  items: BidItem[];
  totalAmount: number;
  currency?: string;
  notes?: string;
  attachments?: Attachment[];
  evaluationCriterias?: EvaluationCriteria[];
  technicalEvaluationMode?: TechnicalEvaluationMode;
  technicalEvaluationThreshold?: number;
  evaluationPhase?: EvaluationPhase;
  approvalType?: 'LOCAL' | 'EXTERNAL';
  steps?: any[];
  externalWorkflow?: any;
}

export interface CreateSourcingInput {
  prIds: string[];
  title?: string;
  description?: string;
  type: SourcingType;
  sourcingScope: SourcingScope;
  invitationMode?: InvitationMode;
  items?: BidItem[];
  totalAmount?: number;
  currency?: string;
  notes?: string;
  attachments?: Attachment[];
  evaluationCriterias?: EvaluationCriteria[];
  technicalEvaluationMode?: TechnicalEvaluationMode;
  technicalEvaluationThreshold?: number;
  evaluationPhase?: EvaluationPhase;
  approvalType?: 'LOCAL' | 'EXTERNAL';
  steps?: any[];
  externalWorkflow?: any;
}

export interface UpdateSourcingInput {
  title?: string;
  description?: string;
  type?: SourcingType;
  sourcingScope?: SourcingScope;
  invitationMode?: InvitationMode;
  items?: BidItem[];
  totalAmount?: number;
  currency?: string;
  notes?: string;
  attachments?: Attachment[];
  evaluationCriterias?: EvaluationCriteria[];
  technicalEvaluationMode?: TechnicalEvaluationMode;
  technicalEvaluationThreshold?: number;
  evaluationPhase?: EvaluationPhase;
  status?: SourcingStatus;
  approvalType?: 'LOCAL' | 'EXTERNAL';
  steps?: any[];
  externalWorkflow?: any;
}

export interface SubmitSourcingInput {
  sourcingId: string;
}

export interface ApproveSourcingInput {
  sourcingId: string;
  remarks?: string;
}

export interface SubmitResultsInput {
  sourcingId: string;
  awardApprovalType?: 'LOCAL' | 'EXTERNAL';
  awardApprovalSteps?: any[];
  awardApprovalFlow?: any;
}

export interface UpdateEvaluationCriteria {
  id: string;
  name?: string;
  description?: string;
  weight?: number;
  visibleToSuppliers?: VisibleToSuppliers;
  threshold?: number;
  type?: EvaluationCriteriaType;
}

export interface SaveItemAwardsInput {
  sourcingId: string;
  itemAwards: SourcingItemAward[];
}